// LocationSearch.tsx
'use client';

import React, { useEffect, useState } from 'react';
import ComponentWrapper from 'helpers/SitecoreWrappers/ComponentWrapper/ComponentWrapper';
import LinkWrapper from 'helpers/SitecoreWrappers/LinkWrapper/LinkWrapper';
import SearchHeaderWidget from 'helpers/Coveo/SearchHeaderWidget';
import { Search, Settings } from '.generated/Feature.CW.model';
import useDictionary from 'lib/hooks/useDictionary';
import PlainTextWrapper from 'helpers/SitecoreWrappers/PlainTextWrapper/PlainTextWrapper';
import { tv } from 'tailwind-variants';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { useSearch } from 'lib/utils/coveo/SearchContext/SearchContext';
import {
  CoveoEngineProvider,
  useCoveoEngine,
} from 'lib/utils/coveo/SearchContext/CoveoEngineContext';
import Pagination from 'helpers/Coveo/Pagination';
import ImageWrapper from 'helpers/SitecoreWrappers/ImageWrapper/ImageWrapper';
import ButtonWrapper from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import { getAddress } from '../content/LocationHeader';
import Sort from 'helpers/Coveo/SortDropdown';
import {
  buildInteractiveResult,
  loadSearchActions,
  loadSearchAnalyticsActions,
} from '@coveo/headless';
import QuerySummary from 'helpers/Coveo/QuerySummary';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import { PlaceholderWrapper } from 'helpers/SitecoreWrappers/PlaceholderWrapper/PlaceholderWrapper';
import FacetGroup from 'helpers/Coveo/FacetGroup';
import classNames from 'classnames';
import AppliedFacetFilters from 'helpers/Coveo/AppliedFacetFilters';
import UrlManagerController from 'helpers/Coveo/UrlManagerController';
import { useRouter } from 'next/router';
import NoResultsMessage from 'helpers/Coveo/NoResultsMessage';

const TAILWIND_VARIANTS = tv({
  slots: {
    base: ['bg-blue-50'],
    headingStyle: ['leading-58 font-bold text-gray-900 font-centuryGothic text-48 lg:mb-0 mb-8'],
    titleLinkWrapperStyle: ['lg:flex justify-between items-center'],
    containerStyle: ['max-w-screen-xxl mx-auto lg:px-[80px] px-10'],
  },
});

type LocationSearchProps = Search.LocationSearch_Component;
interface LocationResultsProps extends LocationSearchProps {
  finalCoordinates?: { latitude: number; longitude: number };
}
export type PageSettings = Settings.SiteSettings['fields'];

const LocationSearch = (props: LocationSearchProps) => {
  const { fields } = props;
  const { getDictionaryValue } = useDictionary();
  const router = useRouter();
  const { lat, lng } = router.query;
  const { sitecoreContext } = useSitecoreContext();
  const contextWithSettings = sitecoreContext?.siteSettings as PageSettings;
  const [_searchTerm, setSearchTerm] = useState('');
  const [locationCoords, setLocationCoords] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [userCoordinates, setUserCoordinates] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [coveoConfig, setCoveoConfig] = useState({
    searchHub: '',
    pipeline: '',
    accessToken: '',
    organizationId: '',
  });
  const handleLocationUpdate = (coords: { latitude: number; longitude: number }) => {
    setLocationCoords(coords);
  };

  const handleSearchTermUpdate = (term: string) => {
    setSearchTerm(term);
  };

  useEffect(() => {
    //debugger;
    setCoveoConfig({
      searchHub: contextWithSettings?.locationsHeaderSearchHub?.value || '',
      pipeline: contextWithSettings?.locationsHeaderPipeline?.value || '',
      accessToken: contextWithSettings?.locationsHeaderAccessToken?.value || '',
      organizationId: process.env.NEXT_PUBLIC_COVEO_ORG_ID || '',
    });
  }, [contextWithSettings]);
  // useEffect(() => {
  //   if (lat && lng) {
  //     const parsedLat = parseFloat(lat as string);
  //     const parsedLng = parseFloat(lng as string);

  //     if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
  //       setLocationCoords({
  //         latitude: parsedLat,
  //         longitude: parsedLng,
  //       });
  //     }
  //   } else {
  //     navigator.geolocation.getCurrentPosition(
  //       (position) => {
  //         setUserCoordinates({
  //           latitude: position.coords.latitude,
  //           longitude: position.coords.longitude,
  //         });
  //       },
  //       (error) => {
  //         console.error('Geolocation error:', error);
  //         alert('Geolocation error: ' + error.message);
  //       }
  //     );
  //   }
  // }, []);
  useEffect(() => {
    if (lat && lng) {
      const parsedLat = parseFloat(lat as string);
      const parsedLng = parseFloat(lng as string);

      if (!isNaN(parsedLat) && !isNaN(parsedLng)) {
        setLocationCoords({
          latitude: parsedLat,
          longitude: parsedLng,
        });
      }
    } else {
      const storedCoords = sessionStorage.getItem('userCoords');
      if (storedCoords) {
        const coords = JSON.parse(storedCoords);
        setUserCoordinates(coords);
      } else {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const coords = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };
            sessionStorage.setItem('userCoords', JSON.stringify(coords));
            setUserCoordinates(coords);
          },
          (error) => {
            console.error('Geolocation error:', error);
            //alert('Geolocation error: ' + error.message);
          }
        );
      }
    }
  }, []);

  // useEffect(() => {
  // }, [contextWithSettings]);

  const searchFields = ['cwspecialties', 'cwpagetype', 'cwtitle'];
  const fieldsToInclude = [
    'cwtemplateid',
    'cwtitle',
    'cwphoto',
    'cwaddress1',
    'cwschedulingtype',
    'cwmobilevisiblephonenumber',
    'cwrequestanappointment',
    'cwschedulingtypecta',
    'clickUri',
    'cwpagetype',
    'cwspecialties',
    'cwcity',
    'cwstate',
    'cwzip',
    'cwaddressmapurl',
    'cwaddressmaptext',
    'distanceinmiles',
    'cwlatitude',
    'cwlongitude',
    'cwitemid',
    'cwepicid',
  ];
  const widgetData = {
    locationSearchPlaceholderText: fields?.locationSearchPlaceholderText,
    searchButtonLabel: getDictionaryValue('SearchButtonLabel'),
    searchTermPlaceholderText: fields?.searchTermPlaceholderText,
    searchLink: fields?.searchButtonLabel,
    searchConfig: coveoConfig,
    searchFields: searchFields,
    fieldsToInclude: fieldsToInclude,
    onLocationCoordinatesChange: handleLocationUpdate,
    onSearchTermChange: handleSearchTermUpdate,
  };

  const finalCoordinates = locationCoords || userCoordinates;
  const enableDistanceSorting = !!finalCoordinates;
  const shouldSortByDistance = enableDistanceSorting;

  if (!coveoConfig.organizationId || !coveoConfig.accessToken || !coveoConfig.pipeline) {
    return null;
  }

  return (
    <CoveoEngineProvider
      config={coveoConfig}
      userCoordinates={finalCoordinates ?? undefined}
      useDistanceSorting={shouldSortByDistance}
      fieldsToInclude={fieldsToInclude}
    >
      <ComponentWrapper className={TAILWIND_VARIANTS().base()}>
        <div className={TAILWIND_VARIANTS().containerStyle()}>
          <div className={TAILWIND_VARIANTS().titleLinkWrapperStyle()}>
            <h1 className={TAILWIND_VARIANTS().headingStyle()}>
              <PlainTextWrapper field={fields?.pageTitle} />
            </h1>
            <LinkWrapper
              field={fields?.findDoctors}
              ctaVariant="tertiary"
              ctaIcon="arrow-right"
              className="text-left w-fit"
            />
          </div>
          <SearchHeaderWidget {...widgetData} hasCoveoWrapper={true} />
        </div>
      </ComponentWrapper>
      <LocationResults {...props} finalCoordinates={finalCoordinates ?? undefined} />
      <UrlManagerController />
    </CoveoEngineProvider>
  );
};

const LocationResults = (props: LocationResultsProps) => {
  const { rendering, params, finalCoordinates } = props;
  const placeholderKey = `location-search-container-${params?.DynamicPlaceholderId}`;
  const { getDictionaryValue } = useDictionary();
  const engine = useCoveoEngine();
  const { ContextResults, setContextResults } = useSearch();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { open } = useAppointmentModal();
  const [firstSearchExecuted, setFirstSearchExecuted] = useState<boolean>(false);

  const fieldsToInclude = [
    'cwtemplateid',
    'cwtitle',
    'cwphoto',
    'cwaddress1',
    'cwschedulingtype',
    'cwmobilevisiblephonenumber',
    'cwrequestanappointment',
    'cwschedulingtypecta',
    'clickUri',
    'cwpagetype',
    'cwspecialties',
    'cwcity',
    'cwstate',
    'cwzip',
    'cwaddressmapurl',
    'cwaddressmaptext',
    'distanceinmiles',
    'cwlatitude',
    'cwlongitude',
    'cwitemid',
    'cwepicid',
  ];

  // useEffect(() => {
  //   if (!engine) return;
  //   let hasExecuted = false;
  //   if (!hasExecuted) {
  //     //engine.executeFirstSearch();
  //     hasExecuted = true;
  //   }
  //   const unsubscribe = engine.subscribe(() => {
  //     setContextResults(engine.state.search);
  //   });
  //   return () => unsubscribe();
  // }, [engine]);

  // keep this code for debug.
  // useEffect(() => {
  //   if (!engine) return;

  //   const unsubscribe = engine.subscribe(() => {
  //     setContextResults(engine.state.search);
  //   });

  //   // Trigger initial search after all controllers (e.g., facets) are mounted
  //   setTimeout(() => {
  //     engine.executeFirstSearch();
  //   }, 0);

  //   return () => unsubscribe();
  // }, [engine]);
  // updated for searchterm and location distance sort search.
  useEffect(() => {
    if (!engine) return;

    let searchExecutedTimeout: NodeJS.Timeout;
    // Trigger initial search after all controllers (e.g., facets) are mounted
    const searchTimeout: NodeJS.Timeout = setTimeout(() => {
      engine.executeFirstSearch();
    }, 100);

    const unsubscribe = engine.subscribe(() => {
      // Clear any existing timeout to prevent multiple state updates
      if (searchExecutedTimeout) {
        clearTimeout(searchExecutedTimeout);
      }
      setContextResults(engine.state.search);

      // Only set the state if we have results or if it's the initial search
      if (engine.state.search.results.length > 0) {
        setFirstSearchExecuted(true);
      } else {
        searchExecutedTimeout = setTimeout(() => {
          setFirstSearchExecuted(true);
        }, 500);
      }
    });
    return () => {
      unsubscribe();
      if (searchTimeout) clearTimeout(searchTimeout);
      if (searchExecutedTimeout) clearTimeout(searchExecutedTimeout);
    };
  }, []);
  // handle location cords - latest
  useEffect(() => {
    if (!engine) return;

    const unsubscribe = engine.subscribe(() => {
      setContextResults(engine.state.search);
    });

    const locationReady = !!finalCoordinates?.latitude && !!finalCoordinates?.longitude;
    const userOverridden =
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem('coveoUserSortOverride') === '1';

    if (locationReady && !userOverridden) {
      const isAlreadyDistance = engine.state.sortCriteria === 'field:distanceinmiles ascending';
      if (!isAlreadyDistance) {
        engine.dispatch({
          type: 'sort/updateSortCriterion',
          payload: { by: 'field', field: 'distanceinmiles', direction: 'ascending' },
        });
      }
      // Force a search so the engine state and dropdown update immediately
      const { executeSearch } = loadSearchActions(engine);
      const { logInterfaceChange } = loadSearchAnalyticsActions(engine);
      engine.dispatch(executeSearch(logInterfaceChange()));
    } else if (!locationReady && !userOverridden) {
      // No coordinates and not overridden: ensure default relevancy
      const isAlreadyRelevancy =
        engine.state.sortCriteria === 'relevancy' ||
        engine.state.sortCriteria === 'relevance' ||
        engine.state.sortCriteria === 'relevancy descending';
      if (!isAlreadyRelevancy) {
        engine.dispatch({
          type: 'sort/updateSortCriterion',
          payload: { by: 'relevance' },
        });
        const { executeSearch } = loadSearchActions(engine);
        const { logInterfaceChange } = loadSearchAnalyticsActions(engine);
        engine.dispatch(executeSearch(logInterfaceChange()));
      }
    }
    return () => {
      unsubscribe();
    };
  }, [engine, finalCoordinates, setContextResults]);

  // useEffect(() => {
  //   if (!engine) return;

  //   const sortCriteria = engine.state.sortCriteria ?? '';
  //   const requiresLocation = sortCriteria.includes('@distanceinmiles');
  //   const locationReady = !!finalCoordinates?.latitude && !!finalCoordinates?.longitude;
  //   const canSearch = !requiresLocation || locationReady;

  //   if (canSearch && !searchExecuted.current) {
  //     engine.executeFirstSearch();
  //     searchExecuted.current = true;

  //     const unsubscribe = engine.subscribe(() => {
  //       const updatedState = engine.state.search;
  //       setContextResults(updatedState);
  //     });

  //     return () => unsubscribe();
  //   }
  //   // Add a default return value for all other paths
  //   return;
  // }, [engine, finalCoordinates, setContextResults]);

  useEffect(() => {
    document.body.style.overflow = isDrawerOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  const results = ContextResults?.results || [];

  return (
    <ComponentWrapper className="bg-white lg:!pt-0">
      <div className="max-w-screen-xxl mx-auto lg:px-[80px] px-10 coveo-search-list">
        <div className="lg:block hidden">
          <FacetGroup
            fields={['cwspecialties', 'cwpagetype', 'cwcity']}
            labels={{ cwspecialties: 'Specialty', cwpagetype: 'Location type', cwcity: 'City' }}
          />
        </div>

        {isDrawerOpen && (
          <div
            className={classNames(
              'fixed inset-0 z-50 bg-white transform transition-transform duration-500 ease-in-out flex flex-col lg:hidden',
              {
                'translate-x-0': isDrawerOpen,
                'translate-x-full': !isDrawerOpen,
              }
            )}
          >
            <div className="flex justify-between items-center px-10 py-[25px] border-b border-gray-200">
              <div className="text-20 font-bold leading-[28px] font-centuryGothic">
                {getDictionaryValue('SetFilters')}
              </div>
              <button onClick={() => setIsDrawerOpen(false)} aria-label="Close">
                <SvgIcon icon="close" size="lg" className="w-10 h-10 [&>path]:fill-blue-base" />
              </button>
            </div>
            <div className="flex-grow overflow-y-auto px-10">
              <FacetGroup
                fields={['cwspecialties', 'cwpagetype', 'cwcity']}
                labels={{ cwspecialties: 'Specialty', cwpagetype: 'Location type', cwcity: 'City' }}
              />
            </div>
            <div className="p-10 border-t border-gray-200">
              <ButtonWrapper
                ctaVariant="primary"
                onClick={() => setIsDrawerOpen(false)}
                className="w-full bg-blue-base text-white py-4 rounded-md text-18 font-semibold"
              >
                {getDictionaryValue('ViewResults')}
              </ButtonWrapper>
            </div>
          </div>
        )}

        <div className="lg:hidden mb-4">
          <ButtonWrapper
            ctaVariant="secondary"
            text="Set Filters"
            ctaIcon="funnel"
            onClick={() => setIsDrawerOpen(true)}
            className="w-full [&>svg]:mt-[-5px] gap-x-4"
            ctaIconAlignment="left"
          />
        </div>

        <div className="py-6">
          <AppliedFacetFilters
            fields={['cwspecialties', 'cwpagetype', 'cwcity']}
            labels={{ cwspecialties: 'Specialty', cwpagetype: 'Location type', cwcity: 'City' }}
          />
        </div>

        <div className="flex flex-wrap justify-between items-center">
          <QuerySummary />
          <Sort
            enableAZSort={true}
            azSortField="cwtitle"
            enableDistanceSort={!!finalCoordinates?.latitude && !!finalCoordinates?.longitude}
            fieldsToInclude={fieldsToInclude}
            finalCoordinates={
              finalCoordinates
                ? { latitude: finalCoordinates.latitude, longitude: finalCoordinates.longitude }
                : undefined
            }
          />
        </div>

        <div className="[&_.inpage-banner-wrapper]:!px-0 [&_.component]:!py-[30px]">
          <PlaceholderWrapper name={placeholderKey} rendering={rendering} />
        </div>

        <div className="resultlist-wrapper">
          {firstSearchExecuted ? (
            results.length > 0 ? (
              <ul className="max-w-screen-xxl w-full mx-auto theme-white">
                {results.map((item, index) => {
                  const schedulingType = (item?.raw?.cwschedulingtype as string)
                    ?.replaceAll(' ', '')
                    ?.toLowerCase();
                  const controller =
                    engine && buildInteractiveResult(engine, { options: { result: item } });
                  return (
                    <li key={index} className="border-b-2 first:border-t-2 border-gray-50">
                      <div
                        role="group"
                        className="py-[30px] gap-y-8 gap-x-24 flex flex-col lg:flex-row"
                      >
                        <div className="flex flex-col md:flex-row gap-8 flex-grow">
                          <div className="flex-grow">
                            <a
                              href={item?.clickUri}
                              onClick={() => controller?.select()}
                              onMouseDown={() => controller?.select()}
                            >
                              <h2 className="text-blue-base font-bold text-28 leading-[31px] font-centuryGothic hover:underline hover:text-darkblue-base block w-full">
                                <PlainTextWrapper field={{ value: item.raw.cwtitle as string }} />
                              </h2>
                            </a>

                            <span className="block mt-5 font-normal text-16 md:text-18 text-gray-900">
                              <PlainTextWrapper field={{ value: item.raw.cwpagetype as string }} />
                            </span>
                            {(item.raw.cwaddress1 as string) && (
                              <div className="flex gap-4 mt-12">
                                <span className="inline-block text-orange-700">
                                  <SvgIcon icon={'map-pin'} size="lg" />
                                </span>
                                <div className="text-18">
                                  {(item.raw.distanceinmiles as number) && (
                                    <div className="font-semibold text-gray-900">
                                      {(item.raw.distanceinmiles as number).toFixed(1)} miles
                                      <span className="text-gray-500 ml-2">
                                        ({getDictionaryValue('EstimatedDistance')})
                                      </span>
                                    </div>
                                  )}

                                  <div className="relative mb-5">
                                    {item.raw.cwaddressmapurl ? (
                                      <LinkWrapper
                                        className="hover:underline text-gray-900 block group "
                                        field={{
                                          value: {
                                            href: item.raw.cwaddressmapurl,
                                            text: ' ', // dummy text to avoid rendering default icon
                                            target: '_blank',
                                          },
                                        }}
                                      >
                                        <div className="group-hover:underline">
                                          {getAddress({
                                            address: item.raw.cwaddress1 as string,
                                            city: (item.raw.cwcity as string) || '',
                                            state: (item.raw.cwstate as string) || '',
                                            zip: (item.raw.cwzip as string) || '',
                                          })}
                                        </div>
                                        <div className="mt-1 inline-flex items-center gap-1 group-hover:underline">
                                          {`${getDictionaryValue('GetDirections')}`}
                                        </div>
                                      </LinkWrapper>
                                    ) : (
                                      <PlainTextWrapper
                                        field={{
                                          value: getAddress({
                                            address: item.raw.cwaddress1 as string,
                                            city: (item.raw.cwcity as string) || '',
                                            state: (item.raw.cwstate as string) || '',
                                            zip: (item.raw.cwzip as string) || '',
                                          }),
                                        }}
                                      />
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                          <div className="block lg:hidden w-full md:min-w-[250px] md:w-[250px]">
                            <ImageWrapper
                              field={{
                                value: {
                                  src: item.raw.cwphoto as string,
                                  alt: item.raw.cwtitle as string,
                                },
                              }}
                              className="aspect-[3/2] w-full rounded-[8px] object-cover"
                            />
                          </div>
                        </div>
                        <div className="flex flex-col md:flex-row gap-y-8 gap-x-24">
                          <div className="lg:min-w-[300px] w-full order-2 md:order-1">
                            <span className="block font-bold text-18 leading-31 mb-6 text-gray-900">
                              {/* todo: add dictionary value for contact us */}
                              {schedulingType &&
                              (schedulingType === 'default' ||
                                schedulingType === 'specialtycare' ||
                                schedulingType === 'hospital' ||
                                schedulingType === 'primarycare' ||
                                schedulingType === 'urgentcare' ||
                                schedulingType === 'mentalhealth' ||
                                (item?.raw?.cwepicid as string)) &&
                              (schedulingType !== 'primarycare'
                                ? item?.raw?.cwschedulingtypecta
                                : true) ? (
                                <PlainTextWrapper
                                  field={{ value: `${getDictionaryValue('ToMakeAnAppointment')}:` }}
                                />
                              ) : (item?.raw?.cwmobilevisiblephonenumber as string) ? (
                                <PlainTextWrapper
                                  field={{ value: `${getDictionaryValue('ContactUs')}:` }}
                                />
                              ) : (
                                <></>
                              )}
                            </span>
                            <div className="flex flex-col md:flex-row lg:flex-col gap-x-8">
                              {schedulingType === 'primarycare' ? (
                                <>
                                  {(item?.raw?.cwepicid as string) && (
                                    <ButtonWrapper
                                      text={getDictionaryValue('ScheduleAnAppointment')}
                                      ctaVariant="primary"
                                      className={`w-full md:w-[calc(50%-12px)] lg:w-full min-w-[300px] ${(item?.raw?.cwmobilevisiblephonenumber as string) ? 'mb-6 md:mb-0 lg:mb-6' : ''}`}
                                      onClick={() =>
                                        open(
                                          item?.raw?.cwitemid
                                            ? (item?.raw?.cwitemid as string)
                                            : '',
                                          `?locationName=${item?.raw?.cwtitle}`,
                                          (item?.raw?.cwmobilevisiblephonenumber as string)
                                            ? (item?.raw?.cwmobilevisiblephonenumber as string)
                                            : ''
                                        )
                                      }
                                    />
                                  )}
                                </>
                              ) : (
                                <>
                                  {item?.raw?.cwschedulingtypecta &&
                                  schedulingType &&
                                  (schedulingType === 'default' ||
                                    schedulingType === 'specialtycare' ||
                                    schedulingType === 'hospital' ||
                                    schedulingType === 'urgentcare' ||
                                    schedulingType === 'mentalhealth') ? (
                                    <LinkWrapper
                                      field={{
                                        value: {
                                          href:
                                            schedulingType === 'urgentcare' ||
                                            schedulingType === 'mentalhealth'
                                              ? item?.raw?.cwschedulingtypecta
                                              : `${item?.raw?.cwschedulingtypecta}?locationName=${item?.raw?.cwtitle}`,
                                          text:
                                            schedulingType === 'urgentcare'
                                              ? getDictionaryValue('ReserveATime')
                                              : getDictionaryValue('RequestAnAppointment'),
                                          title:
                                            schedulingType === 'urgentcare'
                                              ? getDictionaryValue('ReserveATime')
                                              : getDictionaryValue('RequestAnAppointment'),
                                        },
                                      }}
                                      suppressNewTabIcon={true}
                                      ctaVariant="primary"
                                      className={`w-full md:w-[calc(50%-12px)] lg:w-full min-w-[300px] ${(item?.raw?.cwmobilevisiblephonenumber as string) ? 'mb-6 md:mb-0 lg:mb-6' : ''}`}
                                    />
                                  ) : (
                                    <></>
                                  )}
                                </>
                              )}

                              {(item?.raw?.cwmobilevisiblephonenumber as string) && (
                                <LinkWrapper
                                  field={{
                                    value: {
                                      href: `tel:${item?.raw?.cwmobilevisiblephonenumber as string}`,
                                      text: `Call ${item?.raw?.cwmobilevisiblephonenumber as string}`,
                                    },
                                  }}
                                  ctaVariant="secondary"
                                  className="w-full md:w-[calc(50%-12px)] lg:w-full min-w-[300px] !leading-22"
                                />
                              )}
                            </div>

                            {schedulingType === 'primarycare' && (
                              <a
                                href={`${item?.clickUri}#location-staff-list`}
                                className="flex min-w-[300px] justify-start mt-6 text-theme-text-btn-text hover:underline py-7 text-18 leading-25 font-bold font-centuryGothic hover:text-theme-text-btn-hover-text underline-offset-4"
                                target="_self"
                                rel="noopener"
                              >
                                {getDictionaryValue('ViewDoctorsAtThisPractice')}
                              </a>
                            )}
                          </div>
                          <div className="hidden lg:block min-w-[250px] w-[250px] order-1 md:order-2">
                            <ImageWrapper
                              field={{
                                value: {
                                  src: item.raw.cwphoto as string,
                                  alt: item.raw.cwtitle as string,
                                },
                              }}
                              className="aspect-[3/2] w-full rounded-[8px] object-cover"
                            />
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <>
                <NoResultsMessage />
              </>
            )
          ) : null}
        </div>

        <div className="pagination-wrapper">
          <Pagination fieldsToInclude={fieldsToInclude} className="justify-end mt-12" />
        </div>
      </div>
    </ComponentWrapper>
  );
};

// const LocationListing = () => {
//   return <div>LocationListing</div>;
// };

export default LocationSearch;
